import React, { useEffect } from 'react'
import ReactDOMServer from 'react-dom/server'
import { connect } from 'dva'
import Axios from 'axios'
import flatten from 'lodash-es/flatten'
import get from 'lodash-es/get'
import PropTypes from 'prop-types'

import { BASEMAPS, FEATURE_TYPES, MAP_LAYER_IDS, MAX_ZOOM_LEVELS, SIDEBAR_VIEWS, STATES_DATA } from '../../../lib/constants'
import { checkKeyWords, formatDate, setBoundingBox } from '../../../lib/utils'
import { getLayerMetadata } from '../../../lib/historicalImagery'
import { hasLayers, hasSubCategories } from '../../../lib/layers.js'
import { reverseGeocode, stateInfo } from '../../../services/state'
import actions from './actions'
import FeaturePopupContent from './FeaturePopupContent'
import ghAnalytics from '../../../lib/analytics'
import LocationPopupContent from './LocationPopupContent'

/*
  #TODO: This doesn't have to be like this...
  Heavily refactor, preferrably remove with move to FeatureLayers.
  -- TJC - June 29, 2021
*/
function ClickPopupHandler(props) {
  const { activeLayers, activeOverlays, activeTool, historicalItem, isHistoricalView, isTopographicBasemap, isUpdatingLocation, map, view, zoomLevel } = props
  /*
  Single request to retrieve layer or overlay data
  */
  const setDistanceRadius = multiplier => Math.ceil(Math.pow(1.2, (MAX_ZOOM_LEVELS - zoomLevel) * multiplier))
  const buildLayersRequest = async ({ layer, overlay, point }) => {
    let distanceRadius = 10

    if (layer) {
      if (zoomLevel >= 10) distanceRadius = setDistanceRadius(1.75)
    } else if (overlay) {
      distanceRadius = setDistanceRadius(3)
    }

    const config = {
      baseUrl: '',
      params: {
        distance: distanceRadius, // In meters, the radius of our point
        f: 'json',
        geometry: point.toString(),
        geometryType: 'esriGeometryPoint',
        inSR: 4326,
        outFields: '*',
        returnGeometry: 'false',
        spatialRel: 'esriSpatialRelIntersects',
        units: 'esriSRUnit_Meter'
      }
    }

    if (layer && layer.urls && layer.urls.mainRes) {
      const isRoadsOrTrails = checkKeyWords(layer.category, ['roads', 'trails'])
      if (isRoadsOrTrails) {
        const boundingBox = setBoundingBox(point, distanceRadius)
        config.params = {
          f: 'json',
          geometry: { ...boundingBox, spatialReference: { wkid: 102100 } },
          geometryType: 'esriGeometryEnvelope',
          outFields: '*',
          returnGeometry: 'false',
          spatialRel: 'esriSpatialRelIntersects',
          units: 'esriSRUnit_Meter'
        }
      }
      config.url = `${layer.urls.mainRes}/query`
    } else if (overlay) {
      config.url = `${overlay.url}/query`
    }

    if (config.url) {
      return Axios(config)
    }
  }

  const getSearchRequest = async point => {
    const stateInfoRequest = await stateInfo(point, 1)
    let promises = []
    let historicalMetadata = null
    let selectedLayers = []
    let selectedStateName = null
    let selectedStateCounty = null

    if (stateInfoRequest.success) {
      const { data: stateInfoData } = stateInfoRequest.data
      if (stateInfoData.features && stateInfoData.features.length) {
        selectedStateCounty = stateInfoData.features[0].attributes.NAME
        selectedStateName = stateInfoData.features[0].attributes.STATE_NAME
      }
    }
    if (!selectedStateName) {
      const geocodeRequest = await reverseGeocode(`${point[0]}, ${point[1]}`)
      if (geocodeRequest.success) {
        const geocodeData = geocodeRequest.data.data
        if (geocodeData.address && geocodeData.address.PlaceName) {
          selectedStateName = geocodeData.address.PlaceName
        }
      }
    }
    if (isHistoricalView && !isTopographicBasemap) {
      let { center: { x, y }, spatialReference: { latestWkid, wkid }, zoom = 0 } = view
      const params = {
        geometry: { spatialReference: { latestWkid, wkid }, x, y },
        item: historicalItem,
        zoom: zoomLevel || zoom
      }
      historicalMetadata = await getLayerMetadata(params)
    } else {
      for (let ali = 0; ali < activeLayers.length; ali++) {
        if (selectedStateName) {
          const layerStateName = activeLayers[ali].state.toLowerCase()
          if (selectedStateName.toLowerCase() === layerStateName) {
            selectedLayers.push(activeLayers[ali])
            promises.push(buildLayersRequest({ layer: activeLayers[ali], point }))
          }
        } else {
          selectedLayers.push(activeLayers[ali])
          promises.push(buildLayersRequest({ layer: activeLayers[ali], point }))
        }
      }
      for (let aoi = 0; aoi < activeOverlays.length; aoi++) {
        promises.push(buildLayersRequest({ overlay: activeOverlays[aoi], point }))
      }
    }

    return Promise.all(promises)
      .then(response => {
        let layersAtLocation = []
        let layersWithFeatures = []
        let overlaysAtLocation = []
        let overlaysWithFeatures = []

        for (let i = 0; i < response.length; i++) {
          let features = get(response[i], ['data', 'features'], null)
          if (features != null) {
            if (i >= selectedLayers.length) {
              const j = i - selectedLayers.length
              if (features.length >= 1) {
                overlaysWithFeatures.push(activeOverlays[j])
                overlaysAtLocation.push({
                  overlay: activeOverlays[j],
                  metadata: response[i].data
                })
              }
            } else {
              if (features.length >= 1) {
                layersWithFeatures.push(selectedLayers[i])
                layersAtLocation.push({
                  layer: selectedLayers[i],
                  metadata: response[i].data
                })
              }
            }
          }
        }
        ghAnalytics.logLocationInfoViewed(point, layersAtLocation.length)

        if (layersAtLocation.length > 0 || overlaysAtLocation.length > 0) {
          props.gotoLocationSidebar(
            point,
            props.isSidebarClosed,
            layersAtLocation,
            overlaysAtLocation,
            selectedStateCounty,
            selectedStateName
          )
        } else {
          let popUpTemplate = {
            title: 'About this location'
          }
          if (selectedStateCounty) {
            popUpTemplate.title = selectedStateCounty
            if (selectedStateName) {
              popUpTemplate.title += `, ${STATES_DATA[selectedStateName].abbrev}`
            }
          }

          if (isHistoricalView && !isTopographicBasemap) {
            let content = '<span style="font-size: 17px; color: #787878">Not available</span>'
            let tag = '<span style="font-size: 12px">Image Date:</span>'
            if (historicalMetadata?.captureDate) {
              const date = formatDate(historicalMetadata.captureDate, 'MMM D, YYYY')
              content = `<span style="font-size: 17px">${date}</span>`
            }
            popUpTemplate.title = `${tag}\n${content}`
          } else {
            popUpTemplate = {
              ...popUpTemplate,
              location: point,
              content: ReactDOMServer.renderToStaticMarkup(
                <LocationPopupContent activeLayers={selectedLayers} hideCoords={props.hideCoords} layers={layersWithFeatures} point={point} />
              )
            }
          }

          view.popup.alignment = 'auto'
          view.popup.open(popUpTemplate)
        }
      })
      .catch(err => console.error(err))
  }

  const getLocation = (attributes, geometry, point) => {
    switch (attributes.properties.ghType) {
      case FEATURE_TYPES.Point:
        return geometry
      case FEATURE_TYPES.Polyline:
      case FEATURE_TYPES.Polygon:
      case FEATURE_TYPES.Tracks:
        return { latitude: point.latitude, longitude: point.longitude }
      default:
        return { latitude: 0, longitude: 0 }
    }
  }

  const popupEventListener = view.popup.on('trigger-action', event => {
    if (event.action.id == 'feature-clicked') {
      let featureIndex = props.features.findIndex(feat => feat.uuid == event.action.clickedUuid)
      if (featureIndex < 0) {
        return
      } else {
        props.selectFeature(props.features[featureIndex])
        view.popup.close()
      }
    }
  })

  useEffect(() => {
    view.popup.autoOpenEnabled = false
    view.popup.dockEnabled = false
    view.popup.collapseEnabled = false
    view.popup.spinnerEnabled = true
    view.popup.dockOptions = {
      buttonEnabled: false
    }

    let clickListener = null
    const featureLayer = map.findLayerById(MAP_LAYER_IDS.content)
    clickListener = view.on('click', event => {
      if (isUpdatingLocation) return
      // hitTest only looks at the feature layer, since the rest
      // of the finding logic for layers is done through
      // feature services

      /*
        #NOTE: This does not account for the case where a feature is above a piece
        of a layer, though the user could simply hide the feature to click on the layer.
        -- TJC - July 22, 2020
      */
      if (activeTool == null) {
        view
          .hitTest(event, { include: featureLayer })
          .then(response => {
            if (response && response.results && response.results.length > 0) {
              ghAnalytics.logFeatureInfoViewed(event.mapPoint, response.results.length)

              view.popup.alignment = 'top-right'
              // Found a feature. Do the feature popup
              // ONLY SHOWS THE ONE ON "top"
              const result = response.results[0]
              const { attributes, geometry } = result.graphic
              if (attributes == null || attributes.uuid == null) return
              if (attributes.uuid == 'search-point') return

              const location = getLocation(attributes, geometry, event.mapPoint)

              if (attributes.uuid.includes('temp')) {
                view.popup.open({
                  title: 'Feature processing...',
                  location,
                  actions: [],
                  content: ''
                })
                return
              }

              view.popup.open({
                title: attributes.title,
                location,

                maxInlineActions: 5,
                includeDefaultActions: true,

                actions: [
                  {
                    title: 'View Details',
                    id: 'feature-clicked',
                    image: null,
                    clickedUuid: attributes.uuid
                  }
                ],
                content: ReactDOMServer.renderToStaticMarkup(<FeaturePopupContent feature={attributes} hideCoords={props.hideCoords} />)
              })
            } else {
              // Didn't click on a feature. Check if there's layer gubbins.
              const location = {
                latitude: event.mapPoint.latitude,
                longitude: event.mapPoint.longitude,
                elevation: event.mapPoint.z || 0
              }

              view.popup.open({
                title: 'About this location',
                location,
                actions: [],
                content: '',
                promises: [getSearchRequest([location.longitude, location.latitude, location.elevation])]
              })
            }
          })
          .catch(err => console.error(err))
      }
    })

    return () => {
      if (clickListener) {
        clickListener.remove()
      }
      popupEventListener.remove()
    }
  }, [props])

  return null
}

ClickPopupHandler.propTypes = {
  activeLayers: PropTypes.arrayOf(PropTypes.object),
  activeOverlays: PropTypes.arrayOf(PropTypes.object),
  activeTool: PropTypes.any,
  features: PropTypes.arrayOf(PropTypes.object),
  gotoLocationSidebar: PropTypes.any,
  hideCoords: PropTypes.any,
  isHistoricalView: PropTypes.bool,
  isTopographicBasemap: PropTypes.bool,
  historicalItem: PropTypes.object,
  isSidebarClosed: PropTypes.bool,
  isUpdatingLocation: PropTypes.bool,
  map: PropTypes.object,
  selectFeature: PropTypes.any,
  view: PropTypes.object,
  userToken: PropTypes.string,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default connect((model, currentProps) => {
  const { activeTool, basemapMode, isUpdatingLocation, zoomLevel } = model.map
  const { currentUser } = model.user
  const { featuresList: features } = model.features
  const { hideCoords, sidebarView, showSidebar } = model.app
  const { layersByEvent, layersByState, overlays } = model.layers
  const isHistoricalView = sidebarView.view === SIDEBAR_VIEWS.HistoricalImagery
  const isTopographicBasemap = basemapMode === BASEMAPS.Topographic
  const isSidebarClosed = showSidebar === false
  const layersArray = [...layersByEvent, ...layersByState]
  let activeLayers = []
  let activeOverlays = overlays.filter(overlay => overlay.enabled)
  let historicalItem = null
  if (isHistoricalView && !isTopographicBasemap) {
    currentProps.view.popup.headingLevel = 4
    currentProps.view.popup.viewModel.includeDefaultActions = false
    historicalItem = model.historicalImagery.selectedItem
  } else {
    currentProps.view.popup.headingLevel = 3
    currentProps.view.popup.viewModel.includeDefaultActions = true
  }

  layersArray.forEach(layersModel => {
    let categories = layersModel.categories
    let layers = []

    categories.forEach(category => {
      if (hasLayers(category)) {
        let categoryLayers = category.layers
        layers.push(
          categoryLayers.map(layer => {
            return {
              ...layer,
              category: category.categoryName,
              subCategory: null,
              state: layersModel.stateName
            }
          })
        )
      }

      if (hasSubCategories(category)) {
        category.subCategories.forEach(subCat => {
          if (subCat.layers != null) {
            let subCategoryLayers = subCat.layers
            layers.push(
              subCategoryLayers.map(layer => {
                return {
                  ...layer,
                  category: category.categoryName,
                  subCategory: subCat.subCategoryName,
                  state: layersModel.stateName
                }
              })
            )
          }
        })
      }
    })

    layers = flatten(layers)
    activeLayers.push(layers)
  })
  activeLayers = flatten(activeLayers).filter(layer => {
    return layer.control.enabled == 1
  })

  return {
    activeLayers,
    activeOverlays,
    activeTool,
    features,
    hideCoords,
    historicalItem,
    isHistoricalView,
    isSidebarClosed,
    isTopographicBasemap,
    isUpdatingLocation,
    zoomLevel
  }
}, actions)(ClickPopupHandler)
